import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

const TAMANO_OPTIONS = ['Micro', 'Pequeña', 'Mediana', 'Grande', 'Corporación']
const ROL_OPTIONS = ['Líder de Proyecto', 'Analista Senior', 'Analista Junior', 'Consultor', 'Otro']

function completenessScore(ficha) {
  if (!ficha) return 0
  const fields = [
    ficha.tamano_empresa,
    ficha.horizonte_anios,
    ficha.sponsor_nombre,
    ficha.sponsor_cargo,
    ficha.fecha_inicio,
    ficha.fecha_cierre,
    ficha.descripcion_negocio,
    ficha.reto_principal,
    ficha.equipo_nvc && ficha.equipo_nvc.length > 0,
    ficha.unidades_alcance && ficha.unidades_alcance.length > 0,
  ]
  const filled = fields.filter(Boolean).length
  return Math.round((filled / fields.length) * 100)
}

export default function FichaProyecto({ consultancyId }) {
  const [ficha, setFicha]       = useState(null)
  const [open, setOpen]         = useState(false)
  const [editing, setEditing]   = useState(false)
  const [saving, setSaving]     = useState(false)
  const [form, setForm]         = useState({})

  useEffect(() => {
    if (consultancyId) fetchFicha()
  }, [consultancyId])

  async function fetchFicha() {
    const { data } = await supabase
      .from('project_ficha')
      .select('*')
      .eq('consultancy_id', consultancyId)
      .maybeSingle()
    if (data) {
      setFicha(data)
      setForm(data)
    } else {
      // Create stub
      const { data: newFicha } = await supabase
        .from('project_ficha')
        .insert({ consultancy_id: consultancyId, equipo_nvc: [] })
        .select()
        .single()
      setFicha(newFicha)
      setForm(newFicha || {})
    }
  }

  function setField(key, val) {
    setForm(f => ({ ...f, [key]: val }))
  }

  function addEquipoMember() {
    const members = form.equipo_nvc || []
    setField('equipo_nvc', [...members, { nombre: '', rol: '', email: '' }])
  }

  function updateMember(idx, key, val) {
    const members = [...(form.equipo_nvc || [])]
    members[idx] = { ...members[idx], [key]: val }
    setField('equipo_nvc', members)
  }

  function removeMember(idx) {
    const members = (form.equipo_nvc || []).filter((_, i) => i !== idx)
    setField('equipo_nvc', members)
  }

  async function handleSave() {
    setSaving(true)
    const payload = { ...form, updated_at: new Date().toISOString() }
    delete payload.id
    delete payload.created_at
    const { data, error } = await supabase
      .from('project_ficha')
      .update(payload)
      .eq('consultancy_id', consultancyId)
      .select()
      .single()
    setSaving(false)
    if (!error) {
      setFicha(data)
      setForm(data)
      setEditing(false)
    }
  }

  const score = completenessScore(ficha)
  const scoreColor = score >= 80 ? '#10B981' : score >= 40 ? '#F59E0B' : '#EF4444'

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #E5E7EB',
      borderRadius: 12,
      marginBottom: 20,
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
    }}>
      {/* Header row */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '14px 20px',
          cursor: 'pointer',
          background: open ? '#F9FAFB' : '#fff',
          borderBottom: open ? '1px solid #E5E7EB' : 'none',
          gap: 12,
          userSelect: 'none'
        }}
      >
        <span style={{ fontSize: 18 }}>📋</span>
        <span style={{ fontWeight: 600, fontSize: 15, color: '#111827', flex: 1 }}>
          Ficha del Proyecto
        </span>

        {/* Completeness bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 120, height: 6, background: '#E5E7EB', borderRadius: 99 }}>
            <div style={{
              width: `${score}%`,
              height: '100%',
              background: scoreColor,
              borderRadius: 99,
              transition: 'width 0.4s ease'
            }} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: scoreColor, minWidth: 36 }}>
            {score}%
          </span>
        </div>

        <span style={{ color: '#9CA3AF', fontSize: 14 }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{ padding: '20px 24px' }}>
          {editing ? (
            <EditForm
              form={form}
              setField={setField}
              addEquipoMember={addEquipoMember}
              updateMember={updateMember}
              removeMember={removeMember}
              onSave={handleSave}
              onCancel={() => { setForm(ficha); setEditing(false) }}
              saving={saving}
            />
          ) : (
            <ViewMode ficha={ficha} onEdit={() => setEditing(true)} />
          )}
        </div>
      )}
    </div>
  )
}

// ─── View Mode ────────────────────────────────────────────────
function ViewMode({ ficha, onEdit }) {
  if (!ficha) return null
  const fmt = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
  const eq = ficha.equipo_nvc || []

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={onEdit} style={{
          padding: '6px 16px', borderRadius: 8, border: '1px solid #6366F1',
          background: '#EEF2FF', color: '#4F46E5', fontSize: 13, fontWeight: 600, cursor: 'pointer'
        }}>
          ✏️ Editar
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px 24px' }}>
        <Field label="Tamaño empresa" value={ficha.tamano_empresa} />
        <Field label="Horizonte" value={ficha.horizonte_anios ? `${ficha.horizonte_anios} años` : null} />
        <Field label="Empleados" value={ficha.empleados} />
        <Field label="Facturación (USD)" value={ficha.facturacion_usd ? `$${Number(ficha.facturacion_usd).toLocaleString()}` : null} />
        <Field label="Inicio" value={fmt(ficha.fecha_inicio)} />
        <Field label="Cierre estimado" value={fmt(ficha.fecha_cierre)} />
      </div>

      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Sponsor</div>
          {ficha.sponsor_nombre
            ? <div style={{ fontSize: 14, color: '#111827' }}>
                <strong>{ficha.sponsor_nombre}</strong>
                {ficha.sponsor_cargo && <span style={{ color: '#6B7280' }}> · {ficha.sponsor_cargo}</span>}
                {ficha.sponsor_email && <div style={{ fontSize: 12, color: '#9CA3AF' }}>{ficha.sponsor_email}</div>}
              </div>
            : <span style={{ fontSize: 13, color: '#9CA3AF' }}>No definido</span>
          }
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Equipo NVC</div>
          {eq.length > 0
            ? eq.map((m, i) => (
                <div key={i} style={{ fontSize: 13, color: '#111827', marginBottom: 2 }}>
                  <strong>{m.nombre}</strong>
                  {m.rol && <span style={{ color: '#6B7280' }}> · {m.rol}</span>}
                </div>
              ))
            : <span style={{ fontSize: 13, color: '#9CA3AF' }}>No definido</span>
          }
        </div>
      </div>

      {(ficha.descripcion_negocio || ficha.reto_principal) && (
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
          {ficha.descripcion_negocio && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Descripción del Negocio</div>
              <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.5 }}>{ficha.descripcion_negocio}</p>
            </div>
          )}
          {ficha.reto_principal && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Reto Principal</div>
              <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.5 }}>{ficha.reto_principal}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: value ? '#111827' : '#9CA3AF' }}>{value || '—'}</div>
    </div>
  )
}

// ─── Edit Form ───────────────────────────────────────────────
function EditForm({ form, setField, addEquipoMember, updateMember, removeMember, onSave, onCancel, saving }) {
  const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: 7,
    border: '1px solid #D1D5DB', fontSize: 13, color: '#111827',
    outline: 'none', boxSizing: 'border-box', background: '#FAFAFA'
  }
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px 16px', marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Tamaño empresa</label>
          <select value={form.tamano_empresa || ''} onChange={e => setField('tamano_empresa', e.target.value)} style={inputStyle}>
            <option value="">Seleccionar…</option>
            {TAMANO_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Horizonte (años)</label>
          <input type="number" min={1} max={10} value={form.horizonte_anios || ''} onChange={e => setField('horizonte_anios', parseInt(e.target.value) || null)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Empleados</label>
          <input type="number" value={form.empleados || ''} onChange={e => setField('empleados', parseInt(e.target.value) || null)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Facturación (USD)</label>
          <input type="number" value={form.facturacion_usd || ''} onChange={e => setField('facturacion_usd', parseFloat(e.target.value) || null)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Fecha inicio</label>
          <input type="date" value={form.fecha_inicio || ''} onChange={e => setField('fecha_inicio', e.target.value || null)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Fecha cierre estimada</label>
          <input type="date" value={form.fecha_cierre || ''} onChange={e => setField('fecha_cierre', e.target.value || null)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Entrega diagnóstico</label>
          <input type="date" value={form.fecha_diagnostico || ''} onChange={e => setField('fecha_diagnostico', e.target.value || null)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Entrega plan</label>
          <input type="date" value={form.fecha_entrega_plan || ''} onChange={e => setField('fecha_entrega_plan', e.target.value || null)} style={inputStyle} />
        </div>
      </div>

      {/* Sponsor */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Sponsor</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px 12px' }}>
          <div>
            <label style={labelStyle}>Nombre</label>
            <input value={form.sponsor_nombre || ''} onChange={e => setField('sponsor_nombre', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Cargo</label>
            <input value={form.sponsor_cargo || ''} onChange={e => setField('sponsor_cargo', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input type="email" value={form.sponsor_email || ''} onChange={e => setField('sponsor_email', e.target.value)} style={inputStyle} />
          </div>
        </div>
      </div>

      {/* Equipo NVC */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Equipo NVC</div>
          <button onClick={addEquipoMember} style={{
            padding: '3px 10px', borderRadius: 6, border: '1px solid #6366F1',
            background: '#EEF2FF', color: '#4F46E5', fontSize: 12, cursor: 'pointer', fontWeight: 600
          }}>+ Agregar</button>
        </div>
        {(form.equipo_nvc || []).map((m, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '6px 8px', marginBottom: 6 }}>
            <input placeholder="Nombre" value={m.nombre || ''} onChange={e => updateMember(idx, 'nombre', e.target.value)} style={inputStyle} />
            <select value={m.rol || ''} onChange={e => updateMember(idx, 'rol', e.target.value)} style={inputStyle}>
              <option value="">Rol…</option>
              {ROL_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <input placeholder="Email" value={m.email || ''} onChange={e => updateMember(idx, 'email', e.target.value)} style={inputStyle} />
            <button onClick={() => removeMember(idx)} style={{
              padding: '4px 10px', borderRadius: 6, border: '1px solid #FCA5A5',
              background: '#FEF2F2', color: '#EF4444', fontSize: 13, cursor: 'pointer'
            }}>✕</button>
          </div>
        ))}
        {(!form.equipo_nvc || form.equipo_nvc.length === 0) && (
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>Sin miembros. Haz clic en "+ Agregar" para incluir al equipo.</div>
        )}
      </div>

      {/* Contexto */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: 20 }}>
        <div>
          <label style={labelStyle}>Descripción del negocio</label>
          <textarea rows={3} value={form.descripcion_negocio || ''} onChange={e => setField('descripcion_negocio', e.target.value)}
            style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
        <div>
          <label style={labelStyle}>Reto principal</label>
          <textarea rows={3} value={form.reto_principal || ''} onChange={e => setField('reto_principal', e.target.value)}
            style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{
          padding: '8px 20px', borderRadius: 8, border: '1px solid #D1D5DB',
          background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer', fontWeight: 500
        }}>Cancelar</button>
        <button onClick={onSave} disabled={saving} style={{
          padding: '8px 20px', borderRadius: 8, border: 'none',
          background: saving ? '#A5B4FC' : '#6366F1', color: '#fff',
          fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer'
        }}>
          {saving ? 'Guardando…' : 'Guardar Ficha'}
        </button>
      </div>
    </div>
  )
}
